import React, { useMemo } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceArea,
    CartesianGrid,
} from "recharts";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dayLabel(ts) {
    return DAY_LABELS[new Date(ts).getDay()];
}

/**
 * Aggregates a series down to "one point per day" using the daily average.
 * Returns { day, value } points ordered oldest -> newest, last 7 days.
 */
function aggregateByDay(history, key) {
    if (!history || !history.length) return [];
    const byDay = new Map();
    history.forEach((h) => {
        const d = new Date(h.ts);
        d.setHours(0, 0, 0, 0);
        const dayKey = d.getTime();
        if (!byDay.has(dayKey)) byDay.set(dayKey, []);
        byDay.get(dayKey).push(h[key]);
    });
    return Array.from(byDay.entries())
        .sort((a, b) => a[0] - b[0])
        .slice(-7)
        .map(([ts, vals]) => ({
            ts,
            day: dayLabel(ts),
            value: Math.round(vals.reduce((s, v) => s + v, 0) / vals.length),
        }));
}

export const HistoryChart = ({
    history,
    metric = "glucose",
    height = 160,
    testId,
    onPointClick,
}) => {
    const config =
        metric === "glucose"
            ? {
                  key: "value",
                  color: "#0A5C36",
                  label: "Glucose mg/dL",
                  range: [60, 200],
                  safe: [80, 140],
              }
            : {
                  key: "systolic",
                  color: "#0A3D7A",
                  label: "Systolic mmHg",
                  range: [80, 160],
                  safe: [100, 130],
              };

    const data = useMemo(
        () => aggregateByDay(history, config.key),
        [history, config.key],
    );

    const summary = useMemo(() => {
        if (!data.length) return "No readings yet.";
        const values = data.map((d) => d.value);
        const avg = Math.round(values.reduce((s, v) => s + v, 0) / values.length);
        const inRange = values.filter(
            (v) => v >= config.safe[0] && v <= config.safe[1],
        ).length;
        const pct = Math.round((inRange / values.length) * 100);
        return `${data.length}-day average ${avg}. ${pct}% of days in range.`;
    }, [data, config.safe]);

    return (
        <div
            data-testid={testId}
            aria-label={`${config.label} history. ${summary}`}
            role="img"
        >
            <p className="text-xs text-[#5A5A6E] mb-2">{summary}</p>
            <div style={{ width: "100%", height, minWidth: 1, minHeight: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                        onClick={(state) => {
                            if (
                                onPointClick &&
                                state &&
                                state.activePayload &&
                                state.activePayload.length
                            ) {
                                onPointClick(state.activePayload[0].payload);
                            }
                        }}
                        style={{ cursor: onPointClick ? "pointer" : "default" }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#F0EAD6"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="day"
                            stroke="#5A5A6E"
                            tick={{ fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            domain={config.range}
                            stroke="#5A5A6E"
                            tick={{ fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            width={32}
                        />
                        <Tooltip
                            contentStyle={{
                                background: "#1A1A2E",
                                border: "none",
                                borderRadius: 12,
                                color: "#fff",
                                fontSize: 13,
                            }}
                            labelStyle={{ color: "#E0C875", fontWeight: 600 }}
                        />
                        <ReferenceArea
                            y1={config.safe[0]}
                            y2={config.safe[1]}
                            fill="#0A5C36"
                            fillOpacity={0.06}
                        />
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={config.color}
                            strokeWidth={3}
                            dot={{ r: 4, fill: config.color }}
                            activeDot={{ r: 6, fill: "#B8860B" }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default HistoryChart;
